import React, { useState, useEffect } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Typography,
    Box,
    makeStyles,
    CircularProgress,
} from '@material-ui/core';
import { Close, NavigateBefore, NavigateNext } from '@material-ui/icons';
import collectionsApi from '../../../Api/collectionsApi';
import GenericState from '../../GenericLoadingErrorState/GenericState';
import ImageCropper from './ImageCropper';
import { initPdfJs } from '../../../utils/pdfLoader';

const useStyles = makeStyles((theme) => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    dialogContent: {
        padding: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'row',
        height: '60vh',
        maxHeight: '60vh',
        [theme.breakpoints.down('sm')]: {
            height: '50vh',
            maxHeight: '50vh',
        },
    },
    thumbnailGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
        gap: theme.spacing(1.5),
        padding: theme.spacing(2),
        width: '33.33%',
        overflowY: 'auto',
        borderRight: `1px solid ${theme.palette.divider}`,
    },
    thumbnailCard: {
        cursor: 'pointer',
        border: '2px solid transparent',
        borderRadius: theme.spacing(1),
        padding: theme.spacing(1),
        transition: 'all 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
            border: `2px solid ${theme.palette.primary.main}`,
            transform: 'scale(1.05)',
        },
    },
    thumbnailCardSelected: {
        border: `2px solid ${theme.palette.primary.main}`,
        backgroundColor: theme.palette.action.selected,
    },
    thumbnailImageContainer: {
        position: 'relative',
        width: '100%',
        paddingTop: '141.4%',
        overflow: 'hidden',
        backgroundColor: theme.palette.grey[100],
        borderRadius: theme.spacing(0.5),
    },
    thumbnailImage: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'contain',
    },
    pageNumber: {
        textAlign: 'center',
        marginTop: theme.spacing(0.5),
        fontSize: '0.875rem',
        fontWeight: 500,
    },
    previewContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(2),
        width: '66.67%',
        overflowY: 'auto',
    },
    previewImage: {
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
    },
    previewInfo: {
        marginTop: theme.spacing(1),
    },
}));

const PDFNavigationDialog = ({
                                 intl,
                                 open,
                                 onClose,
                                 onCloseAll,
                                 boardId,
                                 isShared,
                                 hideLoadButton = false,
                                 localPdfFile = null,
                             }) => {
    const classes = useStyles();
    const [currentPage, setCurrentPage] = useState(0);
    const [boardData, setBoardData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [imageCropperOpen, setImageCropperOpen] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState(null);
    const [pdfDoc, setPdfDoc] = useState(null);
    const [renderedPageUrl, setRenderedPageUrl] = useState(null);
    const [generatedThumbnails, setGeneratedThumbnails] = useState([]);
    const [thumbnailsLoaded, setThumbnailsLoaded] = useState(0);
    const [isLoadingThumbnails, setIsLoadingThumbnails] = useState(false);
    const [backendThumbnailsDisplayed, setBackendThumbnailsDisplayed] = useState(20);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);
    const loadingTriggerRef = React.useRef(null);
    const thumbnailGridRef = React.useRef(null);

    useEffect(() => {
        if (open && boardId) {
            loadBoardData();
        } else if (open && localPdfFile) {
            loadLocalPdfFile();
        }
    }, [open, boardId, localPdfFile]);

    const loadLocalPdfFile = async () => {
        setIsLoading(true);
        setIsError(false);
        setIsLoadingPreview(true);
        try {
            const fileUrl = URL.createObjectURL(localPdfFile);
            setBoardData({
                name: localPdfFile.name,
                boardType: 'PDF',
                meta: {
                    pdfUrl: fileUrl,
                    pageCount: 0,
                    thumbnails: [],
                },
            });
            setCurrentPage(0);
            await loadPdf(fileUrl);
        } catch (error) {
            console.error('Error loading local PDF file:', error);
            setIsError(true);
        } finally {
            setIsLoading(false);
        }
    };

    const loadBoardData = async () => {
        setIsLoading(true);
        setIsError(false);
        setIsLoadingPreview(true);
        try {
            const response = isShared
                ? await collectionsApi.getSharedBoard(boardId)
                : await collectionsApi.getBoard(boardId);

            if (response.data && response.data.boardType === 'PDF' && response.data.meta && response.data.meta.pdfUrl) {
                setBoardData(response.data);
                setCurrentPage(0);
                await loadPdf(response.data.meta.pdfUrl);
            } else {
                setIsError(true);
            }
        } catch (error) {
            console.error('Error loading PDF board:', error);
            setIsError(true);
        } finally {
            setIsLoading(false);
        }
    };

    const loadPdf = async (pdfUrl) => {
        try {
            const pdfjsLib = await initPdfJs();
            const loadingTask = pdfjsLib.getDocument(pdfUrl);
            const pdf = await loadingTask.promise;
            setPdfDoc(pdf);

            if (localPdfFile) {
                await generateThumbnails(pdf, 1, 20);
            }
        } catch (error) {
            console.error('Error loading PDF:', error);
            setIsError(true);
        }
    };

    const generateThumbnails = async (pdf, startPage = 1, batchSize = 20) => {
        try {
            setIsLoadingThumbnails(true);
            const thumbnails = [];
            const numPages = pdf.numPages;
            const endPage = Math.min(startPage + batchSize - 1, numPages);

            for (let i = startPage; i <= endPage; i++) {
                const page = await pdf.getPage(i);
                const scale = 0.5;
                const viewport = page.getViewport({ scale });
                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');
                canvas.width = viewport.width;
                canvas.height = viewport.height;

                await page.render({ canvasContext: context, viewport }).promise;
                const thumbnailUrl = canvas.toDataURL('image/png');

                thumbnails.push({
                    thumbnailUrl,
                    pageNumber: i,
                });
            }

            setGeneratedThumbnails(prev => [...prev, ...thumbnails]);
            setThumbnailsLoaded(endPage);
        } catch (error) {
            console.error('Error generating thumbnails:', error);
        } finally {
            setIsLoadingThumbnails(false);
        }
    };

    const loadMoreThumbnails = () => {
        if (localPdfFile) {
            if (pdfDoc && !isLoadingThumbnails && thumbnailsLoaded < pdfDoc.numPages) {
                generateThumbnails(pdfDoc, thumbnailsLoaded + 1, 20);
            }
        } else {
            const allThumbnails = boardData?.meta?.thumbnails || [];
            if (backendThumbnailsDisplayed < allThumbnails.length) {
                setIsLoadingThumbnails(true);
                setTimeout(() => {
                    setBackendThumbnailsDisplayed(prev => Math.min(prev + 20, allThumbnails.length));
                    setIsLoadingThumbnails(false);
                }, 100);
            }
        }
    };

    useEffect(() => {
        const allThumbnails = boardData?.meta?.thumbnails || [];
        const hasMoreToLoad = localPdfFile
            ? (pdfDoc && thumbnailsLoaded < pdfDoc.numPages)
            : (backendThumbnailsDisplayed < allThumbnails.length);

        if (!hasMoreToLoad) {
            return;
        }

        // Use setTimeout to ensure refs are attached after render
        const timeoutId = setTimeout(() => {
            const triggerElement = loadingTriggerRef.current;
            const gridElement = thumbnailGridRef.current;

            if (!triggerElement || !gridElement) {
                return;
            }

            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting && !isLoadingThumbnails) {
                            loadMoreThumbnails();
                        }
                    });
                },
                {
                    root: gridElement,
                    rootMargin: '100px',
                    threshold: 0.1,
                },
            );

            observer.observe(triggerElement);

            return () => {
                observer.disconnect();
            };
        }, 100);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [thumbnailsLoaded, isLoadingThumbnails, pdfDoc, localPdfFile, generatedThumbnails, backendThumbnailsDisplayed, boardData]);

    const handlePageClick = (pageIndex) => {
        setRenderedPageUrl(null);
        setIsLoadingPreview(true);
        setCurrentPage(pageIndex);
    };

    const handleLoadPage = () => {
        if (renderedPageUrl) {
            setSelectedImageUrl(renderedPageUrl);
            setImageCropperOpen(true);
        }
    };

    const handleCloseCropper = () => {
        setImageCropperOpen(false);
        setSelectedImageUrl(null);
        if (onCloseAll) {
            onCloseAll();
        } else {
            onClose();
        }
    };

    useEffect(() => {
        if (pdfDoc && currentPage >= 0) {
            renderPage(currentPage + 1);
        }
    }, [pdfDoc, currentPage]);

    const renderPage = async (pageNumber) => {
        try {
            setIsLoadingPreview(true);
            const page = await pdfDoc.getPage(pageNumber);
            const scale = 4.0;
            const viewport = page.getViewport({ scale });
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            await page.render({ canvasContext: context, viewport }).promise;
            const dataUrl = canvas.toDataURL('image/png');
            setRenderedPageUrl(dataUrl);
        } catch (error) {
            console.error('Error rendering page:', error);
        } finally {
            setIsLoadingPreview(false);
        }
    };

    if (!open) return null;

    let content;
    if (isLoading) {
        content = <GenericState isLoading={true} />;
    } else if (isError) {
        content = <GenericState isError={true} />;
    } else if (boardData && pdfDoc) {
        const pageCount = pdfDoc.numPages;
        const allBackendThumbnails = boardData.meta?.thumbnails || [];
        const hasBackendThumbnails = allBackendThumbnails.length > 0;
        const hasGeneratedThumbnails = generatedThumbnails.length > 0;

        // Slice backend thumbnails to only show loaded ones
        const backendThumbnailsToShow = hasBackendThumbnails
            ? allBackendThumbnails.slice(0, backendThumbnailsDisplayed)
            : [];

        const displayThumbnails = hasBackendThumbnails ? backendThumbnailsToShow : generatedThumbnails;
        const hasMoreToLoad = localPdfFile
            ? thumbnailsLoaded < pageCount
            : backendThumbnailsDisplayed < allBackendThumbnails.length;

        content = (
            <>
                {(hasBackendThumbnails || hasGeneratedThumbnails) && (
                    <Box className={classes.thumbnailGrid} ref={thumbnailGridRef}>
                        {displayThumbnails.map((thumb, index) => (
                            <Box
                                key={index}
                                className={`${classes.thumbnailCard} ${
                                    index === currentPage ? classes.thumbnailCardSelected : ''
                                }`}
                                onClick={() => handlePageClick(index)}
                            >
                                <Box className={classes.thumbnailImageContainer}>
                                    <img
                                        src={thumb.thumbnailUrl}
                                        alt={`Page ${index + 1}`}
                                        className={classes.thumbnailImage}
                                    />
                                </Box>
                                <Typography className={classes.pageNumber}>
                                    {index + 1}
                                </Typography>
                            </Box>
                        ))}
                        {hasMoreToLoad && (
                            <Box
                                ref={loadingTriggerRef}
                                className={classes.thumbnailCard}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: '150px',
                                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                    border: '2px dashed rgba(0, 0, 0, 0.2)',
                                    cursor: 'default',
                                }}
                            >
                                {isLoadingThumbnails && (
                                    <CircularProgress size={32} style={{ marginBottom: '8px' }} />
                                )}
                                <Typography variant='body2' color='textSecondary'
                                            style={{ textAlign: 'center', padding: '8px' }}>
                                    {isLoadingThumbnails ?
                                        <FormattedMessage id='pdf.navigation.loading' /> : localPdfFile
                                            ? `${thumbnailsLoaded} / ${pageCount}`
                                            : `${backendThumbnailsDisplayed} / ${allBackendThumbnails.length}`
                                    }
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}
                {(renderedPageUrl || isLoadingPreview) && (
                    <Box className={classes.previewContainer}>
                        {isLoadingPreview ? (
                            <>
                                <CircularProgress size={64} />
                                <Typography variant='body2' color='textSecondary' style={{ marginTop: '16px' }}>
                                    <FormattedMessage id='pdf.navigation.loading' />
                                </Typography>
                            </>
                        ) : (
                            <>
                                <img
                                    src={renderedPageUrl}
                                    alt={`Page ${currentPage + 1}`}
                                    className={classes.previewImage}
                                />
                                <Typography variant='body1' className={classes.previewInfo}>
                                    Page {currentPage + 1} of {pageCount}
                                </Typography>
                            </>
                        )}
                        {!isLoadingPreview && !hasBackendThumbnails && !hasGeneratedThumbnails && (
                            <Box style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                <Button
                                    onClick={() => handlePageClick(Math.max(0, currentPage - 1))}
                                    disabled={currentPage === 0}
                                    variant='outlined'
                                    size='small'
                                >
                                    <NavigateBefore />
                                </Button>
                                <Button
                                    onClick={() => handlePageClick(Math.min(pageCount - 1, currentPage + 1))}
                                    disabled={currentPage === pageCount - 1}
                                    variant='outlined'
                                    size='small'
                                >
                                    <NavigateNext />
                                </Button>
                            </Box>
                        )}
                    </Box>
                )}
            </>
        );
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth='lg'
                fullWidth
            >
                <DialogTitle disableTypography>
                    <Typography variant='h6'>
                        {boardData ? boardData.name : <FormattedMessage id='pdf.navigation.title' />}
                    </Typography>
                    <IconButton
                        aria-label='close'
                        className={classes.closeButton}
                        onClick={onClose}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    {content}
                </DialogContent>
                <DialogActions style={{ padding: '16px 24px' }}>
                    <Button onClick={onClose} variant='contained' color='default'>
                        <FormattedMessage id='close' />
                    </Button>
                    {!hideLoadButton && !isLoading && !isError && boardData && (
                        <Button onClick={handleLoadPage} variant='contained' color='primary'>
                            <FormattedMessage id='pdf.navigation.load' />
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
            <ImageCropper
                dialogImgUploadOpen={imageCropperOpen}
                handleClose={handleCloseCropper}
                preloadedImage={selectedImageUrl}
            />
        </>
    );
};

export default injectIntl(PDFNavigationDialog);
